/*
 * Copyright 2026 Sony Corporation
 */

import { createThrottle } from './throttle';
import { createViewMemory, viewKey, type SavedView, type ViewMemory } from './view-memory';
import type { SceneEditor } from './scene-editor';
import type { SceneSwitcher } from './scene-switcher';

export interface ViewPersistence {
  dispose: () => void;
}

export interface ViewPersistenceOptions {
  editor: SceneEditor;
  switcher: SceneSwitcher;
  worldId: string;
  memory?: ViewMemory;
  throttleMs?: number;
}

/** Orbiting fires every frame; a quarter second is invisible to the user. */
const DEFAULT_THROTTLE_MS = 250;

/**
 * Restores the viewpoint a world was left on, and keeps it current.
 *
 * Saving is suspended while a switch is in flight. The commit reframes the
 * camera to the incoming scene's default view, and that must not be written
 * over the viewpoint the outgoing scene was left on.
 */
export const createViewPersistence = ({
  editor,
  switcher,
  worldId,
  memory = createViewMemory(),
  throttleMs = DEFAULT_THROTTLE_MS,
}: ViewPersistenceOptions): ViewPersistence => {
  let key: string | undefined;

  const throttle = createThrottle<SavedView>({
    intervalMs: throttleMs,
    send: (target, view) => memory.write(target, view),
  });

  const unsubscribe = switcher.subscribe((state) => {
    if (state.status !== 'idle' || !state.activeId) {
      key = undefined;
      return;
    }

    const next = viewKey(worldId, state.activeId);
    if (next === key) {
      return;
    }
    key = next;

    const saved = memory.read(next);
    if (saved) {
      editor.applyView(saved);
    }
  });

  const observer = editor.camera.onViewMatrixChangedObservable.add(() => {
    if (key) {
      throttle.push(key, editor.readView());
    }
  });

  return {
    dispose: () => {
      unsubscribe();
      editor.camera.onViewMatrixChangedObservable.remove(observer);
      // Leaving for the world list is exactly when the pending write matters.
      if (key) {
        throttle.flush(key);
      }
      throttle.cancel();
    },
  };
};
